<script lang="ts" setup>
import { computed } from "vue";
import { RouterLink } from "vue-router";
import type { PictogramName } from "@/shared/@types/PictogramName";
import { ServiceDisplayState } from "@/shared/@types/ServiceDisplayState";
import Icon from "../Icon/Icon.vue";
import Pictogram from "../Pictogram/Pictogram.vue";

const props = withDefaults(
  defineProps<{
    title: string;
    link: string;
    isExternalLink: boolean;
    pictogram: PictogramName;
    previewText: string;
    displayState: ServiceDisplayState;
    backgroundColor?: "white" | "gray";
    showPreviewText?: boolean;
  }>(),
  {
    backgroundColor: "white",
  },
);

const cardComponent = computed(() => {
  if (props.displayState === ServiceDisplayState.Preview) return "a";
  if (props.displayState === ServiceDisplayState.Disabled) return "div";
  if (props.isExternalLink) return "a";
  return RouterLink;
});

interface ConditionalAttributes {
  href?: string;
  target?: string;
  to?: string;
}

const conditionalAttributes = computed<ConditionalAttributes>(() => {
  if (props.displayState === ServiceDisplayState.Disabled) {
    return { to: undefined };
  }

  return props.isExternalLink
    ? { href: props.link, target: "_blank" }
    : { to: props.link };
});

const showPreviewText = computed(
  () =>
    props.displayState === ServiceDisplayState.Disabled ||
    props.displayState === ServiceDisplayState.Preview ||
    props.showPreviewText,
);
</script>

<template>
  <component
    :is="cardComponent"
    v-if="displayState !== ServiceDisplayState.Hidden"
    v-bind="conditionalAttributes"
    class="service-card"
    data-testid="serviceCard"
    :data-display-state="displayState"
    :data-background-color="backgroundColor"
  >
    <Pictogram
      class="service-card--pictogram"
      :pictogram="pictogram"
      size="large"
      :color="
        displayState === ServiceDisplayState.Enabled ? 'red' : 'anthracite'
      "
      remove-padding
    />
    <div>
      <div class="service-card--title-row">
        <p class="service-card--title">{{ title }}</p>
        <Icon
          v-if="
            displayState === ServiceDisplayState.Enabled ||
            displayState === ServiceDisplayState.Preview
          "
          :icon="isExternalLink ? 'openInNew' : 'arrowForward'"
          :size="24"
        />
      </div>
      <p
        v-if="showPreviewText"
        class="service-card--preview-text"
      >
        {{ previewText }}
      </p>
    </div>
  </component>
</template>

<style scoped>
@import "ServiceCard.css";
</style>
